import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import SendIcon from "@mui/icons-material/Send";
import SmsIcon from "@mui/icons-material/Sms";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import ApplicantSmsApi from "../../../../../infrastructure/api/applicantSms";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import JalaliDatePicker from "../../../../components/shared/jalali-datepicker";
import EnhancedTable from "../../../../components/shared/table";
import TabPanel from "../../../../components/shared/tab-panel";
import { toJalaliFullDate } from "../../../../middleware/middleware";

const PAGE_BG = "#F6F8FB";
const CARD_SX = {
  borderRadius: "16px",
  boxShadow: "0 1px 3px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.04)",
};

const normalizeList = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") return Object.values(value);
  return [];
};

const ApplicantSmsPage = () => {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templateKey, setTemplateKey] = useState("");
  const [customMessage, setCustomMessage] = useState("");
  const [useCustom, setUseCustom] = useState(false);
  const [applicantName, setApplicantName] = useState("");
  const [referenceId, setReferenceId] = useState("");
  const [pasteText, setPasteText] = useState("");
  const [singleMobile, setSingleMobile] = useState("");
  const [mobiles, setMobiles] = useState([]);
  const [preview, setPreview] = useState("");
  const [sendResult, setSendResult] = useState(null);

  const [reportLoading, setReportLoading] = useState(false);
  const [reportRows, setReportRows] = useState([]);
  const [reportTotal, setReportTotal] = useState(0);
  const [onlyMine, setOnlyMine] = useState(false);
  const [reportMobile, setReportMobile] = useState("");
  const [senderUserId, setSenderUserId] = useState("");
  const [senders, setSenders] = useState([]);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const selectedTemplate = useMemo(
    () => templates.find((t) => (t.key || t.Key) === templateKey),
    [templates, templateKey]
  );

  const loadTemplates = useCallback(async () => {
    const res = await ApplicantSmsApi.getTemplates();
    if (!res.isSuccess) {
      toast.error(res.message || "بارگذاری قالب‌ها ناموفق بود.");
      return;
    }
    const list = normalizeList(res.data).map((t) => ({
      key: t.key ?? t.Key,
      title: t.title ?? t.Title,
      body: t.body ?? t.Body,
      description: t.description ?? t.Description,
    }));
    setTemplates(list);
    if (!templateKey && list[0]) setTemplateKey(list[0].key);
  }, [templateKey]);

  const loadSenders = useCallback(async () => {
    const res = await ApplicantSmsApi.getSenders();
    if (!res.isSuccess) return;
    setSenders(
      normalizeList(res.data).map((s) => ({
        userId: String(s.userId ?? s.UserId ?? ""),
        userName: s.userName ?? s.UserName ?? "—",
      }))
    );
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    if (tab === 1) loadSenders();
  }, [tab, loadSenders]);

  useEffect(() => {
    if (useCustom) {
      setPreview(customMessage);
      return;
    }
    if (!selectedTemplate?.body) {
      setPreview("");
      return;
    }
    const name = applicantName.trim() ? ` ${applicantName.trim()}` : "";
    const ref = referenceId.trim()
      ? `\nشماره درخواست: ${referenceId.trim()}`
      : "";
    setPreview(
      selectedTemplate.body.replace("{NAME}", name).replace("{REF}", ref)
    );
  }, [useCustom, customMessage, selectedTemplate, applicantName, referenceId]);

  const addMobileToList = async (raw) => {
    const text = String(raw || "").trim();
    if (!text) {
      toast.error("شماره را وارد کنید.");
      return;
    }
    setLoading(true);
    const res = await ApplicantSmsApi.resolveRecipients({
      mobiles: [text],
      rawText: "",
    });
    setLoading(false);
    if (!res.isSuccess) {
      toast.error(res.message || "بررسی شماره ناموفق بود.");
      return;
    }
    const valid = normalizeList(res.data?.validMobiles ?? res.data?.ValidMobiles);
    if (!valid.length) {
      toast.error("شماره موبایل معتبر نیست.");
      return;
    }
    setMobiles((prev) => {
      const next = new Set(prev);
      valid.forEach((m) => next.add(m));
      return Array.from(next);
    });
    setSingleMobile("");
    toast.success("شماره به لیست اضافه شد.");
  };

  const importPaste = async () => {
    if (!pasteText.trim()) {
      toast.error("متنی برای افزودن شماره وارد نشده است.");
      return;
    }
    setLoading(true);
    const res = await ApplicantSmsApi.resolveRecipients({
      rawText: pasteText,
      mobiles: [],
    });
    setLoading(false);
    if (!res.isSuccess) {
      toast.error(res.message || "پردازش شماره‌ها ناموفق بود.");
      return;
    }
    const valid = normalizeList(res.data?.validMobiles ?? res.data?.ValidMobiles);
    const invalid = normalizeList(res.data?.invalidInputs ?? res.data?.InvalidInputs);
    const dup = Number(res.data?.duplicateCount ?? res.data?.DuplicateCount ?? 0);
    setMobiles((prev) => {
      const next = new Set(prev);
      valid.forEach((m) => next.add(m));
      return Array.from(next);
    });
    setPasteText("");
    toast.success(
      `${valid.length} شماره اضافه شد` +
        (invalid.length ? ` — ${invalid.length} نامعتبر` : "") +
        (dup ? ` — ${dup} تکراری` : "")
    );
  };

  const handleSend = async () => {
    if (!mobiles.length) {
      toast.error("حداقل یک شماره به لیست اضافه کنید.");
      return;
    }
    if (useCustom && !customMessage.trim()) {
      toast.error("متن پیامک را وارد کنید.");
      return;
    }
    if (!useCustom && !templateKey) {
      toast.error("یک قالب انتخاب کنید.");
      return;
    }

    setLoading(true);
    setSendResult(null);
    const payload = {
      mobiles,
      message: useCustom ? customMessage.trim() : "",
      templateKey: useCustom ? null : templateKey,
      applicantName: applicantName.trim() || null,
      referenceId: referenceId.trim() ? Number(referenceId.trim()) : null,
      requestId: null,
    };
    const res = await ApplicantSmsApi.send(payload);
    setLoading(false);
    if (!res.isSuccess) {
      toast.error(res.message || "ارسال ناموفق بود.");
      return;
    }
    setSendResult(res.data);
    toast.success(res.message || "ارسال انجام شد.");
    loadReport();
  };

  const loadReport = useCallback(async () => {
    setReportLoading(true);
    const toApiDate = (value) => {
      if (!value) return null;
      if (typeof value === "string") {
        const m = value.trim().match(/^(\d{4}-\d{2}-\d{2})/);
        return m ? `${m[1]}T00:00:00` : value;
      }
      return null;
    };
    const res = await ApplicantSmsApi.getReport({
      mobile: reportMobile.trim() || null,
      startDate: toApiDate(fromDate),
      endDate: toApiDate(toDate),
      onlyMine,
      sentByUserId: senderUserId ? Number(senderUserId) : null,
      pageNumber: page + 1,
      pageSize: rowsPerPage,
    });
    setReportLoading(false);
    if (!res.isSuccess) {
      toast.error(res.message || "بارگذاری گزارش ناموفق بود.");
      return;
    }
    const items = normalizeList(res.data?.items ?? res.data?.Items).map((row) => ({
      id: row.id ?? row.Id,
      mobile: row.mobile ?? row.Mobile,
      message: row.message ?? row.Message,
      purposeTitle: row.purposeTitle ?? row.PurposeTitle,
      statusText: row.statusText ?? row.StatusText,
      createOn: row.createOn ?? row.CreateOn,
      requestId: row.requestId ?? row.RequestId,
      sentByUserName: row.sentByUserName ?? row.SentByUserName ?? "—",
    }));
    setReportRows(items);
    setReportTotal(Number(res.data?.totalCount ?? res.data?.TotalCount ?? items.length));
  }, [reportMobile, fromDate, toDate, onlyMine, senderUserId, page, rowsPerPage]);

  useEffect(() => {
    if (tab === 1) loadReport();
  }, [tab, loadReport]);

  const reportHead = [
    { id: "createOnDisplay", label: "زمان" },
    { id: "sentByUserName", label: "ارسال‌کننده" },
    { id: "mobile", label: "موبایل" },
    { id: "purposeTitle", label: "نوع" },
    { id: "statusText", label: "وضعیت" },
    { id: "messageDisplay", label: "متن" },
  ];

  const reportTableRows = reportRows.map((r) => ({
    ...r,
    createOnDisplay: toJalaliFullDate(r.createOn) || "—",
    messageDisplay:
      (r.message || "").length > 80
        ? `${(r.message || "").slice(0, 80)}…`
        : r.message || "—",
  }));

  const serverPagination = {
    page,
    rowsPerPage,
    totalCount: reportTotal,
    onPageChange: (newPage) => setPage(newPage),
    onRowsPerPageChange: (next) => {
      setRowsPerPage(next);
      setPage(0);
    },
  };

  return (
    <Box sx={{ bgcolor: PAGE_BG, minHeight: "100%", p: { xs: 1.5, md: 2 } }}>
      <BreadcrumbsComponent />
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <SmsIcon color="primary" />
        <Box>
          <Typography variant="h6" fontWeight={700}>
            پیامک به متقاضی
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ارسال قالب آماده یا متن دلخواه، مدیریت لیست شماره، و گزارش ارسال‌ها
          </Typography>
        </Box>
      </Stack>

      <Card sx={CARD_SX}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          allowScrollButtonsMobile
        >
          <Tab label="ارسال پیامک" />
          <Tab label="گزارش ارسال" />
        </Tabs>
        <Divider />

        <TabPanel value={tab} index={0}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={5}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  لیست شماره‌ها
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                  <TextField
                    size="small"
                    fullWidth
                    label="شماره موبایل"
                    placeholder="0912..."
                    value={singleMobile}
                    onChange={(e) => setSingleMobile(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addMobileToList(singleMobile);
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    disabled={loading}
                    onClick={() => addMobileToList(singleMobile)}
                  >
                    افزودن
                  </Button>
                </Stack>
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  minRows={3}
                  label="چسباندن چند شماره"
                  placeholder="هر خط یک شماره، یا با ویرگول جدا کنید"
                  value={pasteText}
                  onChange={(e) => setPasteText(e.target.value)}
                  sx={{ mb: 1 }}
                />
                <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                  <Button
                    variant="outlined"
                    disabled={loading}
                    onClick={importPaste}
                  >
                    افزودن به لیست
                  </Button>
                  <Button
                    color="inherit"
                    disabled={!mobiles.length}
                    startIcon={<DeleteOutlineIcon />}
                    onClick={() => setMobiles([])}
                  >
                    پاک کردن لیست
                  </Button>
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {mobiles.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      هنوز شماره‌ای اضافه نشده است.
                    </Typography>
                  ) : (
                    mobiles.map((m) => (
                      <Chip
                        key={m}
                        label={m}
                        onDelete={() =>
                          setMobiles((prev) => prev.filter((x) => x !== m))
                        }
                      />
                    ))
                  )}
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                  {mobiles.length} شماره در صف ارسال (حداکثر ۱۰۰)
                </Typography>
              </Grid>

              <Grid item xs={12} md={7}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={useCustom}
                      onChange={(e) => setUseCustom(e.target.checked)}
                    />
                  }
                  label="نوشتن متن دلخواه"
                />

                {!useCustom ? (
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="پیام آماده"
                    value={templateKey}
                    onChange={(e) => setTemplateKey(e.target.value)}
                    sx={{ mb: 1.5, mt: 1 }}
                  >
                    {templates.map((t) => (
                      <MenuItem key={t.key} value={t.key}>
                        {t.title}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <TextField
                    fullWidth
                    multiline
                    minRows={5}
                    label="متن پیامک"
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    sx={{ mb: 1.5, mt: 1 }}
                    helperText={`${customMessage.length} / ۹۰۰ کاراکتر`}
                  />
                )}

                <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      label="نام متقاضی (اختیاری)"
                      value={applicantName}
                      onChange={(e) => setApplicantName(e.target.value)}
                      disabled={useCustom}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      label="شماره درخواست (اختیاری)"
                      value={referenceId}
                      onChange={(e) => setReferenceId(e.target.value)}
                      disabled={useCustom}
                    />
                  </Grid>
                </Grid>

                <Alert severity="info" sx={{ mb: 1.5 }} icon={false}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    spacing={1}
                  >
                    <Box sx={{ whiteSpace: "pre-wrap", flex: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        پیش‌نمایش
                      </Typography>
                      <Typography variant="body2">{preview || "—"}</Typography>
                    </Box>
                    <IconButton
                      size="small"
                      disabled={!preview}
                      onClick={() => {
                        navigator.clipboard?.writeText(preview);
                        toast.info("متن کپی شد.");
                      }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Alert>

                <Button
                  variant="contained"
                  size="large"
                  startIcon={loading ? <CircularProgress size={18} /> : <SendIcon />}
                  disabled={loading || !mobiles.length}
                  onClick={handleSend}
                >
                  ارسال به {mobiles.length || 0} شماره
                </Button>

                {sendResult ? (
                  <Alert
                    sx={{ mt: 2 }}
                    severity={
                      Number(sendResult.failedCount ?? sendResult.FailedCount) > 0
                        ? "warning"
                        : "success"
                    }
                  >
                    موفق: {sendResult.successCount ?? sendResult.SuccessCount} — ناموفق:{" "}
                    {sendResult.failedCount ?? sendResult.FailedCount}
                  </Alert>
                ) : null}
              </Grid>
            </Grid>
          </CardContent>
        </TabPanel>

        <TabPanel value={tab} index={1}>
          <CardContent>
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  size="small"
                  fullWidth
                  label="فیلتر موبایل"
                  value={reportMobile}
                  onChange={(e) => setReportMobile(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  size="small"
                  fullWidth
                  label="کارشناس"
                  value={senderUserId}
                  onChange={(e) => {
                    setSenderUserId(e.target.value);
                    setOnlyMine(false);
                    setPage(0);
                  }}
                  disabled={onlyMine}
                >
                  <MenuItem value="">همه کارشناسان</MenuItem>
                  {senders.map((s) => (
                    <MenuItem key={s.userId} value={s.userId}>
                      {s.userName}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <JalaliDatePicker
                  label="از تاریخ"
                  value={fromDate}
                  onChange={setFromDate}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <JalaliDatePicker
                  label="تا تاریخ"
                  value={toDate}
                  onChange={setToDate}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={onlyMine}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setOnlyMine(checked);
                          if (checked) setSenderUserId("");
                          setPage(0);
                        }}
                      />
                    }
                    label="فقط ارسال‌های من"
                  />
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    disabled={reportLoading}
                    onClick={() => {
                      setPage(0);
                      loadReport();
                    }}
                  >
                    جستجو
                  </Button>
                </Stack>
              </Grid>
            </Grid>

            <EnhancedTable
              headCells={reportHead}
              rows={reportTableRows}
              loading={reportLoading}
              showFilter={false}
              pageSize={rowsPerPage}
              serverPagination={serverPagination}
            />
          </CardContent>
        </TabPanel>
      </Card>
    </Box>
  );
};

export default ApplicantSmsPage;
